import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth, ROLE_SUPER_ADMIN, ROLE_ORG_ADMIN, ROLE_ORG_USER } from '../contexts/AuthContext';
import { adminAPI, organizationsAPI } from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import { 
  Users, 
  Plus, 
  Search,
  Loader2,
  Edit2,
  Trash2,
  Shield,
  UserCog,
} from 'lucide-react';
import { toast } from 'sonner';
import Sidebar from '../components/Sidebar';

export default function AdminUsersPage() {
  const { user, isSuperAdmin, isAdmin, organizationName } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/dashboard');
      return;
    }
    fetchUsers();
  }, [isAdmin, navigate]);

  const fetchUsers = async () => {
    try {
      const response = await adminAPI.getUsers();
      setUsers(response.data);
    } catch (error) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    try {
      await adminAPI.deleteUser(deleteId);
      setUsers(users.filter(u => u.id !== deleteId));
      toast.success('User deleted successfully');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleteId(null);
    }
  };

  const filteredUsers = users.filter(u => 
    u.name.toLowerCase().includes(search.toLowerCase()) ||
    u.email.toLowerCase().includes(search.toLowerCase())
  );

  const getRoleBadge = (role) => {
    switch (role) {
      case ROLE_SUPER_ADMIN:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-body font-medium bg-primary text-white">
            <Shield className="w-3 h-3" />
            Super Admin
          </span>
        );
      case ROLE_ORG_ADMIN:
        return (
          <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-body font-medium bg-accent text-white">
            <UserCog className="w-3 h-3" />
            Org Admin
          </span>
        );
      default:
        return (
          <span className="px-2 py-1 rounded-full text-xs font-body font-medium bg-secondary text-foreground">
            User
          </span>
        );
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Sidebar />

      {/* Main Content */}
      <main className="ml-0 md:ml-64 p-4 md:p-8 pt-16 md:pt-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-heading text-2xl font-bold text-foreground tracking-tight">
              User Management
            </h1>
            <p className="font-body text-muted-foreground">
              {isSuperAdmin ? 'Manage all user accounts' : `Manage users for ${organizationName}`}
            </p>
          </div>
          <Link to="/admin/users/new">
            <Button className="btn-lift bg-primary text-white font-body" data-testid="add-user-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add User
            </Button>
          </Link>
        </div>

        {/* Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or email..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10 font-body bg-card"
              data-testid="search-users-input"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-card rounded-xl border border-border overflow-hidden">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
            </div>
          ) : filteredUsers.length === 0 ? (
            <div className="text-center py-16">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" strokeWidth={1.5} />
              <p className="font-body text-muted-foreground mb-4">No users found</p>
              <Link to="/admin/users/new">
                <Button className="btn-lift bg-primary text-white font-body" data-testid="add-first-user-btn">
                  <Plus className="w-4 h-4 mr-2" />
                  Add First User
                </Button>
              </Link>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow className="bg-secondary/50">
                  <TableHead className="font-heading font-semibold">Name</TableHead>
                  <TableHead className="font-heading font-semibold">Email</TableHead>
                  {isSuperAdmin && <TableHead className="font-heading font-semibold">Organization</TableHead>}
                  <TableHead className="font-heading font-semibold">Role</TableHead>
                  <TableHead className="font-heading font-semibold">Status</TableHead>
                  <TableHead className="font-heading font-semibold text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredUsers.map((u) => (
                  <TableRow key={u.id} className="hover:bg-secondary/30 transition-colors" data-testid={`user-row-${u.id}`}>
                    <TableCell className="font-body font-medium">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                          <span className="font-heading text-sm font-semibold text-primary">
                            {u.name?.charAt(0)?.toUpperCase() || '?'}
                          </span>
                        </div>
                        {u.name}
                        {u.id === user?.id && (
                          <span className="text-xs text-muted-foreground">(you)</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="font-body text-muted-foreground">
                      {u.email}
                    </TableCell>
                    {isSuperAdmin && (
                      <TableCell className="font-body text-muted-foreground">
                        {u.organization_name || '-'}
                      </TableCell>
                    )}
                    <TableCell>
                      {getRoleBadge(u.role)}
                    </TableCell>
                    <TableCell>
                      <span className={`px-2 py-1 rounded-full text-xs font-body font-medium ${
                        u.is_active ? 'bg-success text-white' : 'bg-muted text-muted-foreground'
                      }`}>
                        {u.is_active ? 'Active' : 'Disabled'}
                      </span>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Link to={`/admin/users/${u.id}/edit`}>
                          <Button variant="ghost" size="sm" className="text-primary" data-testid={`edit-user-${u.id}`}>
                            <Edit2 className="w-4 h-4" />
                          </Button>
                        </Link>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          className="text-error hover:text-error"
                          onClick={() => setDeleteId(u.id)}
                          disabled={u.id === user?.id}
                          data-testid={`delete-user-${u.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </main>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle className="font-heading">Delete User</AlertDialogTitle>
            <AlertDialogDescription className="font-body">
              Are you sure you want to delete this user? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="font-body" data-testid="cancel-delete-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={handleDelete} 
              className="bg-error text-white hover:bg-error/90 font-body"
              data-testid="confirm-delete-btn"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
